/**
 * Événements PostHog produit, noms stables pour HogQL / agents.
 * Source de vérité : shared/product-analytics/event-catalog.yaml
 */

export const ProductAnalyticsFunnels = {
  PRO_ACTIVATION: 'pro_activation',
  MONETIZATION: 'monetization',
  BOOKING_LOOP: 'booking_loop',
  ONBOARDING: 'onboarding',
} as const;

export type ProductAnalyticsFunnel =
  (typeof ProductAnalyticsFunnels)[keyof typeof ProductAnalyticsFunnels];

export const ProductAnalyticsModules = {
  SIGNUP: 'signup',
  RESERVATIONS: 'reservations',
  BILLING: 'billing',
  FORFAITS: 'forfaits',
  STRIPE: 'stripe',
  SERVICES: 'services',
  ONBOARDING: 'onboarding',
  ANIMALS: 'animals',
  PLANNING: 'planning',
  LAIKA: 'laika',
  MARKETING: 'marketing',
  PARTNERS: 'partners',
} as const;

export const ProductAnalyticsEvents = {
  PAGE_VIEW: '$pageview',
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGGED_OUT: 'user_logged_out',
  SIGNUP_STARTED: 'signup_started',
  SIGNUP_COMPLETED: 'signup_completed',
  PRO_SIGNUP_STARTED: 'pro_signup_started',
  PRO_SIGNUP_COMPLETED: 'pro_signup_completed',
  PRO_PROFILE_CREATED: 'pro_profile_created',
  ONBOARDING_WELCOME_SHOWN: 'onboarding_welcome_shown',
  ONBOARDING_WELCOME_CLOSED: 'onboarding_welcome_closed',
  ONBOARDING_WELCOME_CTA_CLICKED: 'onboarding_welcome_cta_clicked',
  ONBOARDING_VIEWED: 'onboarding_viewed',
  ONBOARDING_STEP_CLICKED: 'onboarding_step_clicked',
  ONBOARDING_DISMISSED: 'onboarding_dismissed',
  ONBOARDING_STEP_MARKED_DONE: 'onboarding_step_marked_done',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  SUBSCRIPTION_CHECKOUT_STARTED: 'subscription_checkout_started',
  SUBSCRIPTION_CHECKOUT_RETURNED: 'subscription_checkout_returned',
  SUBSCRIPTION_UPGRADE_CLICKED: 'subscription_upgrade_clicked',
  SUBSCRIPTION_ACTIVATED: 'subscription_activated',
  SUBSCRIPTION_UPDATED: 'subscription_updated',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  BOOKING_CREATION_STARTED: 'booking_creation_started',
  BOOKING_CREATION_COMPLETED: 'booking_creation_completed',
  BOOKING_CREATED: 'booking_created',
  SERVICE_CREATION_COMPLETED: 'service_creation_completed',
  SERVICE_CREATED: 'service_created',
  QUOTE_CREATION_COMPLETED: 'quote_creation_completed',
  QUOTE_CREATED: 'quote_created',
  FORFAIT_CREATED: 'forfait_created',
  FORFAIT_SOLD: 'forfait_sold',
  GIFT_VOUCHER_CREATED: 'gift_voucher_created',
  GIFT_VOUCHER_SENT: 'gift_voucher_sent',
  GIFT_VOUCHER_REDEEMED: 'gift_voucher_redeemed',
  BILLING_PAGE_VIEWED: 'billing_page_viewed',
  BILLING_BLOCKED_STRIPE_SHOWN: 'billing_blocked_stripe_shown',
  /** PostHog prop `source`: settings | startup | billing (Billing soft CTA #555) */
  STRIPE_ONBOARDING_CTA_CLICKED: 'stripe_onboarding_cta_clicked',
  STRIPE_ACCOUNT_LINK_STARTED: 'stripe_account_link_started',
  STRIPE_SETUP_RETURNED: 'stripe_setup_returned',
  STRIPE_CONNECT_COMPLETED: 'stripe_connect_completed',
  FIRST_INVOICE_DRAFT_CREATED: 'first_invoice_draft_created',
  FIRST_INVOICE_FINALIZED_OR_SENT: 'first_invoice_finalized_or_sent',
  VIEWED_ANIMAL_DETAILS: 'viewed_animal_details',
  POLICE_REGISTER_EXPORTED: 'police_register_exported',
  PLANNING_VIEW_SEEN: 'planning_view_seen',
  PLANNING_VIEW_TOGGLED: 'planning_view_toggled',
  AVAILABILITY_SCHEDULE_CREATED: 'availability_schedule_created',
  BLOG_ARTICLE_VIEW: 'blog_article_view',
  BLOG_ARTICLE_SCROLL: 'blog_article_scroll',
  BLOG_ARTICLE_CTA_CLICKED: 'blog_article_cta_clicked',
  BLOG_ARTICLE_RELATED_CLICKED: 'blog_article_related_clicked',
  BLOG_ARTICLE_SHARE: 'blog_article_share',
  LAIKA_CHAT_OPENED: 'laika_chat_opened',
  LAIKA_MESSAGE_SENT: 'laika_message_sent',
  MARKETING_HUB_TAB_VIEWED: 'marketing_hub_tab_viewed',
  OUTBOUND_CONFIGURE_CLICKED: 'outbound_configure_clicked',
  AUTOMATION_TOGGLED: 'automation_toggled',
  MARKETING_IDEAS_CAPTION_COPIED: 'marketing_ideas_caption_copied',
  MARKETING_IDEAS_SLOT_COPIED: 'marketing_ideas_slot_copied',
  MARKETING_IDEAS_REVIEW_COPIED: 'marketing_ideas_review_copied',
  MARKETING_IDEAS_STORY_OPENED: 'marketing_ideas_story_opened',
  MARKETING_IDEAS_STORY_DOWNLOADED: 'marketing_ideas_story_downloaded',
  DATA_POSTER_SHARED: 'data_poster_shared',
  PARTNER_PAGE_VIEWED: 'partner_page_viewed',
  PARTNER_CODE_UNLOCKED_VIEWED: 'partner_code_unlocked_viewed',
  PARTNER_UPGRADE_CTA_CLICKED: 'partner_upgrade_cta_clicked',
  IDENTITY_SECTION_VIEWED: 'identity_section_viewed',
  IDENTITY_SAVED: 'identity_saved',
  IDENTITY_SEED_CLICKED: 'identity_seed_clicked',
  AMBASSADOR_ENROLLED: 'ambassador_enrolled',
  AMBASSADOR_CODE_COPIED: 'ambassador_code_copied',
  WHATS_NEW_TOUR_SHOWN: 'whats_new_tour_shown',
  WHATS_NEW_TOUR_DISMISSED: 'whats_new_tour_dismissed',
  WHATS_NEW_TOUR_ARTICLE_CLICKED: 'whats_new_tour_article_clicked',
} as const;

/**
 * Valeurs documentées de `source` pour `stripe_onboarding_cta_clicked`
 * (SettingsPage, StartupPage, BillingPage soft CTA).
 */
export const STRIPE_ONBOARDING_CTA_SOURCES = [
  'settings',
  'startup',
  'billing',
] as const;

export type StripeOnboardingCtaSource =
  (typeof STRIPE_ONBOARDING_CTA_SOURCES)[number];

/** @deprecated Préférer ProductAnalyticsEvents, alias rétrocompatibilité */
export const BackofficeAnalyticsEvents = ProductAnalyticsEvents;

export type ProductAnalyticsEventName =
  (typeof ProductAnalyticsEvents)[keyof typeof ProductAnalyticsEvents];

export type BackofficeAnalyticsEventName = ProductAnalyticsEventName;

/** Liste ordonnée pour tests de parité avec product_analytics_events.py */
export const PRODUCT_ANALYTICS_EVENT_NAMES: readonly string[] = [
  '$pageview',
  'user_logged_in',
  'user_logged_out',
  'signup_started',
  'signup_completed',
  'pro_signup_started',
  'pro_signup_completed',
  'pro_profile_created',
  'onboarding_welcome_shown',
  'onboarding_welcome_closed',
  'onboarding_welcome_cta_clicked',
  'onboarding_viewed',
  'onboarding_step_clicked',
  'onboarding_dismissed',
  'onboarding_step_marked_done',
  'onboarding_completed',
  'subscription_checkout_started',
  'subscription_checkout_returned',
  'subscription_upgrade_clicked',
  'subscription_activated',
  'subscription_updated',
  'subscription_cancelled',
  'booking_creation_started',
  'booking_creation_completed',
  'booking_created',
  'service_creation_completed',
  'service_created',
  'quote_creation_completed',
  'quote_created',
  'forfait_created',
  'forfait_sold',
  'gift_voucher_created',
  'gift_voucher_sent',
  'gift_voucher_redeemed',
  'billing_page_viewed',
  'billing_blocked_stripe_shown',
  'stripe_onboarding_cta_clicked',
  'stripe_account_link_started',
  'stripe_setup_returned',
  'stripe_connect_completed',
  'first_invoice_draft_created',
  'first_invoice_finalized_or_sent',
  'viewed_animal_details',
  'police_register_exported',
  'planning_view_seen',
  'planning_view_toggled',
  'availability_schedule_created',
  'blog_article_view',
  'blog_article_scroll',
  'blog_article_cta_clicked',
  'blog_article_related_clicked',
  'blog_article_share',
  'laika_chat_opened',
  'laika_message_sent',
  'marketing_hub_tab_viewed',
  'outbound_configure_clicked',
  'automation_toggled',
  'marketing_ideas_caption_copied',
  'marketing_ideas_slot_copied',
  'marketing_ideas_review_copied',
  'marketing_ideas_story_opened',
  'marketing_ideas_story_downloaded',
  'data_poster_shared',
  'partner_page_viewed',
  'partner_code_unlocked_viewed',
  'partner_upgrade_cta_clicked',
  'identity_section_viewed',
  'identity_saved',
  'identity_seed_clicked',
  'ambassador_enrolled',
  'ambassador_code_copied',
  'whats_new_tour_shown',
  'whats_new_tour_dismissed',
  'whats_new_tour_article_clicked',
];
